/**
 * API communication service
 */

// Fetch Matomo configuration
export const fetchMatomoConfig = async () => {
  try {
    const response = await fetch('/api/config/matomo');
    if (!response.ok) {
      throw new Error('Failed to fetch Matomo configuration');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching Matomo configuration:', error);
    return { url: '', siteId: '' }; // Return empty config in case of error
  }
};

// Fetch a random background image from the backend
export const fetchRandomBackgroundImage = async () => {
  try {
    const response = await fetch('/api/background');
    if (!response.ok) {
      throw new Error('Failed to fetch background image');
    }
    const data = await response.json();

    // Return the path directly - the App component will handle it
    return data.imagePath;
  } catch (error) {
    console.error('Error fetching background image:', error);
    // Return a default background color instead of an empty string
    return '#1a1a2e'; // Dark blue background color
  }
};

// Cache for API responses
const apiCache = {
  mapData: null,
  sounds: null,
  userSettings: null,
  challenge: null,
  pendingRequests: {}
};

// Helper function to deduplicate requests
const deduplicateRequest = async (cacheKey, requestFn) => {
  // If we have a cached response, return it
  if (apiCache[cacheKey]) {
    console.log(`Using cached ${cacheKey}`);
    return apiCache[cacheKey];
  }

  // If there's already a pending request for this key, wait for it
  if (apiCache.pendingRequests[cacheKey]) {
    console.log(`Waiting for pending ${cacheKey} request`);
    return apiCache.pendingRequests[cacheKey];
  }

  // Create a new promise for this request
  console.log(`Making new request for ${cacheKey}`);
  apiCache.pendingRequests[cacheKey] = requestFn().then(result => {
    // Cache the result
    apiCache[cacheKey] = result;
    // Clear the pending request
    delete apiCache.pendingRequests[cacheKey];
    // Return the result
    return result;
  }).catch(error => {
    // Clear the pending request on error
    delete apiCache.pendingRequests[cacheKey];
    // Rethrow the error
    throw error;
  });

  // Return the promise
  return apiCache.pendingRequests[cacheKey];
};

// Clear cache for a specific key or all keys
export const clearApiCache = (key = null) => {
  if (key) {
    console.log(`Clearing cache for ${key}`);
    apiCache[key] = null;
  } else {
    console.log('Clearing all API cache');
    apiCache.mapData = null;
    apiCache.sounds = null;
    apiCache.userSettings = null;
    apiCache.challenge = null;
  }
};

// Fetch map data from the server
export const fetchMapData = async () => {
  return deduplicateRequest('mapData', async () => {
    try {
      const response = await fetch('/api/maps');
      if (!response.ok) {
        throw new Error('Failed to fetch map data');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching map data:', error);
      return {}; // Return empty object in case of error
    }
  });
};

// Fetch current user information
export const fetchCurrentUser = async () => {
  try {
    const response = await fetch('/api/user', {
      credentials: 'include' // Important for sending cookies with the request
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching user:', error);
    return null;
  }
};

// Fetch user scores
export const fetchUserScores = async () => {
  try {
    const response = await fetch('/api/user/scores', {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch user scores');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching user scores:', error);
    throw error; // Re-throw the error for error handling
  }
};

// Fetch a new challenge
export const fetchNewChallenge = async (difficulty, sessionId = null, gamemode = null) => {
  // For challenges, we don't use the cache because each challenge should be unique
  // However, we still want to deduplicate requests to prevent multiple identical requests

  // Build the URL with query parameters
  let url = `/api/challenge?difficulty=${difficulty}`;
  if (sessionId) {
    url += `&sessionId=${sessionId}`;
  }
  if (gamemode) {
    url += `&gamemode=${gamemode}`;
  }

  // Create a unique cache key for this specific challenge request
  const cacheKey = `challenge_${difficulty}_${sessionId || 'new'}_${gamemode || 'default'}`;

  // Check if we have a pending request for this exact challenge
  if (apiCache.pendingRequests[cacheKey]) {
    console.log(`Waiting for pending challenge request: ${cacheKey}`);
    return apiCache.pendingRequests[cacheKey];
  }

  // Create a new promise for this request
  console.log(`Making new challenge request: ${cacheKey}`);
  apiCache.pendingRequests[cacheKey] = (async () => {
    try {
      const response = await fetch(url);

      // If response is not ok, handle specific error cases
      if (!response.ok) {
        // Try to parse the error response
        const errorData = await response.json().catch(() => ({}));

        // Create an error object with additional information
        const error = new Error(errorData.message || 'Error fetching challenge');
        error.response = {
          status: response.status,
          data: errorData
        };

        throw error;
      }

      const result = await response.json();

      // Clear the pending request
      delete apiCache.pendingRequests[cacheKey];

      return result;
    } catch (error) {
      console.error('Error:', error);

      // Clear the pending request on error
      delete apiCache.pendingRequests[cacheKey];

      // If this is a specific error like daily limit, rethrow it
      if (error.response && error.response.status === 403) {
        throw error;
      }

      // Fallback to a mock response for other errors
      return {
        id: 'challenge_' + Math.floor(Math.random() * 1000),
        image: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAMCAgMCAgMDAwMEAwMEBQgFBQQEBQoHBwYIDAoMDAsKCwsNDhIQDQ4RDgsLEBYQERMUFRUVDA8XGBYUGBIUFRT/wAARC...'
      };
    }
  })();

  return apiCache.pendingRequests[cacheKey];
};

// Submit position to the backend
export const submitPosition = async (gameId, map, position, sessionId = null) => {
  // Prepare the data to send
  const data = {
    gameId,
    map,
    position,
    sessionId
  };

  try {
    const response = await fetch('/api/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    // Check if it's a map mismatch or time expired error
    if (response.status === 400) {
      const errorData = await response.json();
      if (errorData.error === 'Map mismatch') {
        // If the map is wrong, return 0 points
        return {
          score: 0,
          distance: -1,
          actualPosition: {
            x: 0,
            y: 0,
            description: 'Wrong map selected'
          }
        };
      } else if (errorData.error === 'time_expired') {
        // If time has expired, return a specific error object
        return {
          score: 0,
          distance: -1,
          actualPosition: {
            x: 0,
            y: 0,
            description: 'Time expired. Your submission was not accepted.'
          },
          timeExpired: true
        };
      }
      throw new Error('Error submitting position');
    }

    if (!response.ok) {
      throw new Error('Error submitting position');
    }

    return await response.json();
  } catch (error) {
    console.error('Error:', error);
    // Fallback to a mock response in case of error
    return {
      score: Math.floor(Math.random() * 5000),
      distance: Math.floor(Math.random() * 500),
      actualPosition: {
        x: Math.random() * 1000,
        y: Math.random() * 800,
        description: 'Simulated position (error occurred)'
      }
    };
  }
};

// Finalize game session
export const finalizeGameSession = async (sessionId) => {
  if (!sessionId) {
    console.warn('No session ID found, cannot finalize game session');
    return null;
  }

  try {
    const response = await fetch(`/api/game/finalize/${sessionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to finalize game session');
    }
    const finalizedSession = await response.json();
    return finalizedSession;
  } catch (error) {
    console.error('Error finalizing game session:', error);
    return null;
  }
};

// Fetch daily leaderboard
export const fetchDailyLeaderboard = async (difficulty = 'hard', category = null) => {
  try {
    let url = `/api/leaderboard/daily?difficulty=${difficulty}`;
    if (category) {
      url += `&category=${encodeURIComponent(category)}`;
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch daily leaderboard');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching daily leaderboard:', error);
    return [];
  }
};

// Fetch weekly leaderboard
export const fetchWeeklyLeaderboard = async (difficulty = 'hard', category = null) => {
  try {
    let url = `/api/leaderboard/weekly?difficulty=${difficulty}`;
    if (category) {
      url += `&category=${encodeURIComponent(category)}`;
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch weekly leaderboard');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching weekly leaderboard:', error);
    return [];
  }
};

// Fetch available categories
export const fetchCategories = async () => {
  try {
    const response = await fetch('/api/gamemode-categories');
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

// Fetch user settings
export const fetchUserSettings = async () => {
  return deduplicateRequest('userSettings', async () => {
    try {
      const response = await fetch('/api/user/settings', {
        credentials: 'include' // Important for sending cookies with the request
      });
      if (!response.ok) {
        throw new Error('Failed to fetch user settings');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching user settings:', error);
      return { exclude_from_stats: false }; // Default settings in case of error
    }
  });
};

// Update user settings
export const updateUserSettings = async (settings) => {
  try {
    const response = await fetch('/api/user/settings', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settings),
      credentials: 'include' // Important for sending cookies with the request
    });
    if (!response.ok) {
      throw new Error('Failed to update user settings');
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating user settings:', error);
    throw error;
  }
};

// Fetch available sounds
export const fetchSounds = async () => {
  return deduplicateRequest('sounds', async () => {
    try {
      const response = await fetch('/api/sounds', {
        credentials: 'include' // Important for sending cookies with the request
      });
      if (!response.ok) {
        throw new Error('Failed to fetch sounds');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching sounds:', error);
      return []; // Return empty array in case of error
    }
  });
};

// Logout the current user
export const logout = async () => {
  try {
    const response = await fetch('/auth/logout', {
      credentials: 'include' // Important for sending cookies with the request
    });
    if (!response.ok) {
      throw new Error('Failed to logout');
    }
    // Redirect to home page after successful logout
    window.location.href = '/';
    return true;
  } catch (error) {
    console.error('Error logging out:', error);
    return false;
  }
};
